import { Fragment, useState, type PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { useGetProfile } from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { ContentFull, ContentLayout } from "@/components/ContentLayout";
import { ContentNotFound } from "@/components/ContentNotFound";
import { DragColumn } from "@/components/drag-columns/components/DragColumn";
import { DragContainer } from "@/components/drag-columns/components/DragContainer";
import { DragItem } from "@/components/drag-columns/components/DragItem";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { FullScreenLayout } from "@/components/FullScreenLayout";
import { IconButton } from "@/components/IconButton";
import { RootRedirect } from "@/components/RootRedirect";
import { TextLoading } from "@/components/TextLoading";

import { AddEventWidget } from "./components/cards/AddEventWidget";
import { EventLayoutCardContent } from "./components/cards/EventWidgetCard";
import { EventWidgetsContext } from "./contexts/EventWidgetsContext";
import { useCurrentEvent, useEventInviteCode } from "./hooks/useCurrentEvent";
import { useEventWidgets, type ActiveWidget } from "./hooks/useEventWidgets";

export function EventPage() {
  const params = useParams();
  const eventId = Number.parseInt(params.eventId!, 10);
  if (Number.isNaN(eventId)) {
    throw new TypeError("Page not found");
  }

  return (
    <Fragment key={eventId}>
      <EventPageProvider>
        <EventPageContent />
      </EventPageProvider>
    </Fragment>
  );
}

function EventPageProvider({ children }: PropsWithChildren) {
  const draftLayoutState = useState<ActiveWidget[] | null>(null); // oxlint-disable-line hook-use-state
  return <EventWidgetsContext.Provider value={draftLayoutState}>{children}</EventWidgetsContext.Provider>;
}

function EventPageContent() {
  const { t } = useTranslation();
  const event = useCurrentEvent({ query: { select: (data) => data.data } });

  const { layout, isLayoutModified, moveWidget, canMove, saveLayout, resetLayout, addWidget } = useEventWidgets();

  const me = useGetProfile("@me", { query: { select: (data) => data.data.profile } });
  const invite = useEventInviteCode();
  const isHost = event.isSuccess && me.isSuccess && event.data.event.account_id === me.data.id;

  if (me.isError && !invite) {
    return <RootRedirect />;
  }

  return (
    <FullScreenLayout>
      {!!event.data && <meta property="og:title" content={event.data.event.name || t("untitled-event")} />}
      {!!event.data?.event.description && <meta property="og:description" content={event.data.event.description.raw} />}

      <ContentLayout title={event.data ? event.data.event.name || t("untitled-event") : <TextLoading />} wet>
        <ErrorBoundary fallback={ContentNotFound}>
          <ContentFull className="flex flex-col">
            <div className="flex h-26 flex-row items-end justify-end p-2">
              {isLayoutModified && (
                <ActionBox>
                  <IconButton type="button" name="x" aria-label={t("cancel")} onClick={resetLayout} />
                  <IconButton type="button" name="save" aria-label={t("save")} onClick={saveLayout} />
                </ActionBox>
              )}
            </div>
            <DragContainer className="flex grow grid-cols-2 flex-col gap-4 sm:grid" onMove={moveWidget}>
              <DragColumn column={0} className="gap-4 self-stretch">
                {layout
                  .filter((w) => w.column === 0)
                  .toSorted((a, b) => a.row - b.row)
                  .map((item, row) => (
                    <DragItem key={item.id} drag={isHost && canMove} row={row}>
                      <EventLayoutCardContent widget={item} />
                    </DragItem>
                  ))}
              </DragColumn>
              <DragColumn column={1} className="gap-4 self-stretch">
                {layout
                  .filter((w) => w.column === 1)
                  .toSorted((a, b) => a.row - b.row)
                  .map((item, row) => (
                    <DragItem key={item.id} drag={isHost && canMove} row={row}>
                      <EventLayoutCardContent widget={item} />
                    </DragItem>
                  ))}
                {isHost && !isLayoutModified && <AddEventWidget className="order-last" onAdd={addWidget} />}
              </DragColumn>
            </DragContainer>
          </ContentFull>
        </ErrorBoundary>
      </ContentLayout>
    </FullScreenLayout>
  );
}
